import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import axios from 'axios';
import { Form } from '@tesla/informed-tds';
import { Loader } from '@tesla/design-system-react';
import { useSearchParams } from 'react-router-dom';
import { ERROR_LOG_TYPES, logSentryError } from '../../utils/sentry';
import { DataContext, QueryContext } from './context';
import { storeMainData } from '../../actions';
import { Error, Filters, ResultsContainer } from '../../components';
import style from './style.module.scss';
import { Api } from './Api';
import { createQueryParamsFromObject } from '../../utils/common';

const Search = () => {
  const formApiRef = React.createRef();
  const [searchParams] = useSearchParams();
  const apiUrl = useSelector((state) => state.app.stateUrl);
  const isGeoIPEnabled = useSelector((state) => state.app.isGeoIpEnabled);
  const geoIpUrl = useSelector((state) => state.app.geoIpUrl);
  const jobData = useSelector((state) => state.main.data);
  const dispatch = useDispatch();
  const store = useCallback((data) => dispatch(storeMainData(data)), [dispatch]);

  const [dataContextValue, setDataContextValue] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [loadingState, setLoadingState] = useState('loading');
  const [queryContextValue, setQueryContextValue] = useState({});
  const [geoIpData, setGeoIpData] = useState(null);

  const loadJobs = async (initialSearchParams) => {
    try {
      const response = await axios.get(apiUrl);
      if (response && response.status === 200) {
        const { data } = response;
        store(data);
        const api = new Api(data);

        const initialDepartment = initialSearchParams.get('department');
        if (initialDepartment) {
          // Check if incoming department is not an id then we look up the id by slug
          if (Number.isNaN(parseInt(initialDepartment, 10))) {
            const departmentId = api.lookupDepartmentIdBySlug(initialDepartment);
            if (departmentId) {
              if (formApiRef.current) {
                formApiRef.current.setValue('department', departmentId);
              }
              setInitialValues((prevInitialValues) => {
                return {
                  ...prevInitialValues,
                  department: departmentId,
                };
              });
            }
          } else {
            // If the incoming department is an id then we switch the department in the url to a slug
            const departmentSlug = api.lookupDepartmentSlugById(initialDepartment);
            if (departmentSlug) {
              createQueryParamsFromObject({
                ...Object.fromEntries(initialSearchParams),
                department: departmentSlug,
              });
            }
          }
        }

        setDataContextValue(api);
        setLoadingState('done');
      } else {
        setLoadingState('error');
      }
    } catch (e) {
      logSentryError('CAREERS_RETRIEVE_JOBS_FAILURE', ERROR_LOG_TYPES.ERROR, e);
      setLoadingState('error');
    }
  };

  const loadGeoIpData = async () => {
    try {
      const response = await axios.get(geoIpUrl);
      if (response && response.status === 200) {
        setGeoIpData(response.data);
      }
    } catch (e) {
      logSentryError('CAREERS_RETRIEVE_GEO_DATA_FAILURE', ERROR_LOG_TYPES.ERROR);
    }
  };

  useEffect(() => {
    // This effect checks if we want to prefill the country dropdown with info we get from geoip call.
    // If the user lands on a URL with region=X, we need to make sure the country from geoip is actually IN that
    // region, otherwise the provided region filter gets overridden by the country filter.
    if (dataContextValue && geoIpData) {
      const countryCode = get(geoIpData, 'country.iso_code', null);
      if (formApiRef.current) {
        const currentRegion = formApiRef.current.getState().values.region || null;
        const currentCountry = formApiRef.current.getState().values.site || null;
        if (!currentCountry && dataContextValue.isCountryInRegion(countryCode, currentRegion)) {
          formApiRef.current.setValue('site', countryCode);
        }
      } else if (!initialValues.site && countryCode) {
        setInitialValues((prevInitialValues) => {
          const { region = null } = prevInitialValues;
          if (!region || dataContextValue.isCountryInRegion(countryCode, region)) {
            return {
              ...prevInitialValues,
              site: countryCode,
            };
          }

          return prevInitialValues;
        });
      }
    }
  }, [dataContextValue, geoIpData]);

  useEffect(async () => {
    if (!apiUrl && !jobData) {
      setLoadingState('error');
      return;
    }

    let api = null;
    if (jobData !== null) {
      api = new Api(jobData);
    }

    // Set the initial values of the form, as these come
    // from the URL, we can do this on mount.
    let queryFromUrl = '';
    const filterKeys = [
      'query',
      'department',
      'region',
      'site',
      'country',
      'location',
      'state',
      'type',
    ];
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of searchParams.entries()) {
      if (filterKeys.indexOf(key) >= 0) {
        // CNDE-5242: Change parameter name of "country" to "site"
        if (key && key.toLowerCase() === 'country')
          setInitialValues((prevInitialValues) => ({ ...prevInitialValues, site: value }));
        if (key && key.toLowerCase() === 'department' && Number.isNaN(parseInt(key, 10)) && api)
          setInitialValues((prevInitialValues) => ({
            ...prevInitialValues,
            department: api.lookupDepartmentIdBySlug(value),
          }));
        else setInitialValues((prevInitialValues) => ({ ...prevInitialValues, [key]: value }));
      }

      if (key === 'query') {
        queryFromUrl = value;
      }
    }

    // Create Query Context
    setQueryContextValue({
      query: initialValues.query || queryFromUrl,
      setQuery: (newQuery) => {
        setQueryContextValue((prevQueryContextValue) => ({
          ...prevQueryContextValue,
          query: newQuery,
        }));
      },
    });

    if (jobData !== null) {
      setDataContextValue(api);
      setLoadingState('done');
      return;
    }

    if (isGeoIPEnabled && geoIpUrl) {
      await loadGeoIpData();
    }

    await loadJobs(searchParams);
  }, []);

  switch (loadingState) {
    case 'loading':
      return <Loader show="true" />;
    case 'error':
      return <Error />;
    case 'done':
    default:
      return (
        <div className="careers-width-constraint">
          <DataContext.Provider value={dataContextValue}>
            <QueryContext.Provider value={queryContextValue}>
              <Form initialValues={initialValues} formApiRef={formApiRef}>
                <div className="tds-layout tds-layout-2col-static_sidebar tds-layout-main--right tds-o-careers-layout">
                  <main className="tds-layout-item tds-layout-main">
                    <ResultsContainer query={queryContextValue.query} />
                  </main>
                  <aside className="tds-layout-item tds-layout-aside">
                    <div className={`tds-scrim--light ${style.careersFilters}`}>
                      <Filters
                        resetFilters={() => {
                          setInitialValues({});
                          formApiRef.current.reset();
                        }}
                      />
                    </div>
                  </aside>
                </div>
              </Form>
            </QueryContext.Provider>
          </DataContext.Provider>
        </div>
      );
  }
};

export default Search;
